import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  Polyline,
  useJsApiLoader,
  Autocomplete,
  InfoWindow,
  OverlayView
} from '@react-google-maps/api';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  InputBase,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Collapse,
  CircularProgress
} from '@mui/material';
import EditVehicleModal from '../Vehicles/edit-vehicle-modal.tsx';
import TripsListDrawer from './trip-list-card.tsx';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NearMeIcon from '@mui/icons-material/NearMe';
import CircleIcon from '@mui/icons-material/Circle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FileClockOutline from 'mdi-material-ui/FileClockOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CallIcon from '@mui/icons-material/Call';
import institutionIcon from '../../../assets/institution.png';
import { FaFilePen } from "react-icons/fa6";
import HandshakeIcon from '@mui/icons-material/Handshake';
import mapsIcon from '../../../assets/map.png';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Draggable from 'react-draggable';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { toast } from 'react-toastify';
import { getCurrentTrip, getVehicleRoute, getCurrentLocation, getVehicle, getInstitutionVehicleMapping } from './action.ts';
import TripDetailsModal from './trip-details-modal.tsx';

const containerStyle = { width: '100%', height: '100vh' };
const libraries: ("places" | "geometry")[] = ['places', 'geometry'];
const DEFAULT_CENTER = { lat: 12.834932, lng: 77.606037 };

const isTimestampToday = (timestampStr: string | undefined) => {
  if (!timestampStr) return false;
  const date = new Date(timestampStr.replace(' ', 'T'));
  const today = new Date();
  
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const formatEventTime = (timestampStr: string | undefined) => {
  if (!timestampStr) return "Time unknown";
  
  const date = new Date(timestampStr.replace(" ", "T"));
  const timeString = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  
  if (isTimestampToday(timestampStr)) {
    return timeString; 
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month} - ${timeString}`; 
  }
};

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const getDistanceInMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  if (!window.google) return Infinity;
  const p1 = new window.google.maps.LatLng(lat1, lng1);
  const p2 = new window.google.maps.LatLng(lat2, lng2);
  return window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
};

const formatStopDuration = (duration_sec: number) => {
  const totalMins = Math.round(duration_sec / 60);
  if (totalMins <= 0) return "< 1 min";
  if (totalMins <= 60) return `${totalMins} mins`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins > 0 ? `${hrs} hr ${mins} min` : `${hrs} hr`;
};

const processAndFilterStops = (stopEvents: any[] = [], idleEvents: any[] = [], firstMovingEvents: any[] = []) => {
  const combined = [
    ...stopEvents.map((e) => ({ ...e, category: 'stop' })),
    ...idleEvents.map((e) => ({ ...e, category: 'idle' }))
  ].map((event) => ({
    lat: Number(event.start_latitude),
    lng: Number(event.start_longitude),
    timestamp: event.category === 'stop' ? (event.actual_start_time || event.start_time || "") : (event.start_time || ""),
    type: event.type || "stop",
    category: event.category,
    duration_sec: event.duration_sec || 0,
    geofence_name: event.geofence_name || null
  }));

  combined.sort((a, b) => {
    const timeA = new Date(a.timestamp.replace(' ', 'T')).getTime();
    const timeB = new Date(b.timestamp.replace(' ', 'T')).getTime();
    return timeA - timeB;
  });

  const filteredStops: any[] = [];
  let globalSequence = 1;
  let lastModifiedMarker: any = null;

  combined.forEach((current, index) => {
    const eventData = {
      timestamp: current.timestamp,
      duration_sec: current.duration_sec,
      type: current.type,
      category: current.category,
      sequenceNumber: 0,
      geofence_name: current.geofence_name
    };

    if (filteredStops.length === 0) {
      eventData.sequenceNumber = globalSequence++;
      current.groupedEvents = [eventData];
      current.hasGeofence = !!current.geofence_name;
      current.hasStop = current.category === 'stop';
      filteredStops.push(current);
      lastModifiedMarker = current;
      return;
    }

    const previousRawEvent = combined[index - 1];
    const isSameGeofence = current.geofence_name && previousRawEvent.geofence_name === current.geofence_name;
    const isCloseDistance = getDistanceInMeters(current.lat, current.lng, previousRawEvent.lat, previousRawEvent.lng) <= 100;

    if (isSameGeofence || isCloseDistance) {
      lastModifiedMarker.duration_sec += current.duration_sec;

      if (lastModifiedMarker.groupedEvents && lastModifiedMarker.groupedEvents.length > 0) {
         const lastGroupedEvent = lastModifiedMarker.groupedEvents[lastModifiedMarker.groupedEvents.length - 1];
         lastGroupedEvent.duration_sec += current.duration_sec;
         if (current.category === 'stop') {
             lastModifiedMarker.hasStop = true;
             lastModifiedMarker.category = 'stop';
             lastGroupedEvent.category = 'stop';
             lastGroupedEvent.type = current.type;
         }
      }
      return;
    }

    let grouped = false;

    if (current.geofence_name) {
      const existingGeofenceIndex = filteredStops.findIndex(
        (kept) => kept.geofence_name === current.geofence_name
      );

      if (existingGeofenceIndex !== -1) {
        eventData.sequenceNumber = globalSequence++;
        filteredStops[existingGeofenceIndex].groupedEvents.push(eventData);
        filteredStops[existingGeofenceIndex].hasGeofence = true;
        
        if (current.category === 'stop') {
          filteredStops[existingGeofenceIndex].hasStop = true;
        }
        grouped = true;
        lastModifiedMarker = filteredStops[existingGeofenceIndex];
      }
    }

    if (!grouped) {
      eventData.sequenceNumber = globalSequence++;
      current.groupedEvents = [eventData];
      current.hasGeofence = !!current.geofence_name;
      current.hasStop = current.category === 'stop';
      filteredStops.push(current);
      lastModifiedMarker = current;
    }
  });

  return filteredStops;
};

interface SharedGoogleMapProps {
  mapContainerStyle?: React.CSSProperties;
  mapCenter: { lat: number; lng: number };
  zoom?: number;
  options?: any;
  onLoad?: (map: google.maps.Map) => void;
  directions?: google.maps.DirectionsResult | null;
  showPolyline?: boolean;
  tripPath?: { lat: number; lng: number }[];
  tripStops?: { 
      lat: number; 
      lng: number; 
      timestamp: string; 
      type?: string; 
      category?: string;
      duration_sec?: number | null; 
      geofence_name?: string | null; 
      groupedEvents?: any[];
      hasGeofence?: boolean;
      hasStop?: boolean;
  }[];
  tripStartPos?: { lat: number; lng: number; timestamp?: string } | null;
  tripEndPos?: { lat: number; lng: number } | null;
  originPos?: { lat: number; lng: number; timestamp?: string } | null;
  destPos?: { lat: number; lng: number } | null;
  currentRoutePos?: { lat: number; lng: number; timestamp?: string } | null; 
  isDirectionsTab?: boolean;
  institutionData?: any[];
}

const SharedGoogleMap: React.FC<SharedGoogleMapProps> = ({
  mapContainerStyle = containerStyle,
  mapCenter,
  zoom = 13,
  options,
  onLoad,
  directions,
  showPolyline,
  tripPath = [],
  tripStops = [],
  tripStartPos,
  tripEndPos,
  originPos,
  destPos,
  currentRoutePos,
  isDirectionsTab = false,
  institutionData = []
}) => {
  const [selectedStop, setSelectedStop] = useState<any>(null);
  
  useEffect(() => {
    if (isDirectionsTab || tripStops.length === 0) {
      setSelectedStop(null);
    }
  }, [isDirectionsTab, tripStops]);

  const getArrowRotation = () => {
    if (!window.google) return 0;

    if (directions && directions.routes[0]?.overview_path?.length > 1) {
      return window.google.maps.geometry.spherical.computeHeading(
        directions.routes[0].overview_path[0],
        directions.routes[0].overview_path[1]
      );
    }

    if (tripPath && tripPath.length > 1) {
      return window.google.maps.geometry.spherical.computeHeading(
        new window.google.maps.LatLng(tripPath[0].lat, tripPath[0].lng),
        new window.google.maps.LatLng(tripPath[1].lat, tripPath[1].lng)
      );
    }

    return 0;
  };

  const finalDestPos = showPolyline ? tripEndPos : destPos;
  const finalStartPos = showPolyline ? tripStartPos : originPos;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={zoom}
      onLoad={onLoad}
      options={options}
      onClick={() => setSelectedStop(null)}
    >
      {directions && !showPolyline && (
        <DirectionsRenderer
          directions={directions}
          options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#1a73e8', strokeWeight: 6 } }}
        />
      )}

     {showPolyline && window.google && (
        <Polyline
          path={tripPath}
          options={{
            strokeColor: '#1a73e8',
            strokeOpacity: 1,
            strokeWeight: 5,
            icons: [
              {
                icon: {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 2,
                  strokeColor: '#424242',
                  fillColor: '#424242',
                  fillOpacity: 1,
                },
                offset: '0',
                repeat: '100px', 
              },
            ],
          }}
        />
      )}

     {tripStops.map((stop, index) => {
         const markerColor = stop.hasGeofence ? "#2e7d32" : (stop.hasStop ? "#FF0000" : "#FFA500");
         
         return (
            <Marker
              key={`stop-${index}`}
              position={{ lat: stop.lat, lng: stop.lng }}
              onClick={() => setSelectedStop({ ...stop, index })}
              onMouseOver={() => setSelectedStop({ ...stop, index })}
              label={{ 
                  text: (stop.groupedEvents?.[0]?.sequenceNumber || (index + 1)).toString(), 
                  color: "white", 
                  fontSize: "12px", 
                  fontWeight: "bold" 
              }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: markerColor,
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: "white",
              }}
            />
         )
     })}

      {selectedStop && (
        <InfoWindow
          position={{ lat: selectedStop.lat, lng: selectedStop.lng }}
          onCloseClick={() => setSelectedStop(null)}
          options={{ pixelOffset: new window.google.maps.Size(0, -10), disableAutoPan: true }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", mb: 1 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, lineHeight: 1.2, color: "text.primary" }}>
                {selectedStop?.geofence_name || (selectedStop?.type === 'start' ? 'Start Location' : (selectedStop?.type === 'current' ? 'Current Location' : (selectedStop?.hasStop ? 'Stop Location' : 'Idle Location')))}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setSelectedStop(null)} 
                sx={{ ml: 1, p: 0.2, fontSize: "14px", mt: -0.5, mr: -0.5 }}
              >
                ✕
              </IconButton>
            </Box>

           {selectedStop?.groupedEvents && selectedStop.groupedEvents.length > 0 ? (
              selectedStop.groupedEvents.map((evt: any, idx: number) => {
                const isMultipleEvents = selectedStop.groupedEvents.length > 1;
                const innerBadgeColor = evt.geofence_name ? "#2e7d32" : (evt.category === 'stop' ? '#FF0000' : '#FFA500');

                return (
                  <Box 
                    key={idx} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      py: 0.5, 
                      borderBottom: idx !== selectedStop.groupedEvents.length - 1 ? '1px solid #e0e0e0' : 'none' 
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      
                      {isMultipleEvents && (
                        <Box 
                          sx={{ 
                            width: '18px', 
                            height: '18px', 
                            borderRadius: '50%', 
                            backgroundColor: innerBadgeColor,
                            color: 'white', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            fontSize: '10px', 
                            fontWeight: 'bold',
                            marginRight: '8px',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}
                        >
                          {evt.sequenceNumber}
                        </Box>
                      )}

                      <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                        {formatEventTime(evt.timestamp)}
                      </Typography>
                    </Box>

                    {evt.duration_sec !== undefined && evt.duration_sec !== null && (
                      <Typography sx={{ fontSize: "11px", color: "text.secondary", ml: 2, fontWeight: 500 }}>
                        {formatStopDuration(evt.duration_sec)}
                      </Typography>
                    )}
                  </Box>
                );
              })
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                  {formatEventTime(selectedStop?.timestamp)}
                </Typography>
                {selectedStop?.duration_sec !== undefined && selectedStop?.duration_sec !== null && (
                  <Typography sx={{ fontSize: "11px", color: "text.secondary", ml: 2, fontWeight: 500 }}>
                    {formatStopDuration(selectedStop.duration_sec)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </InfoWindow>
      )}

      {finalStartPos && (
        <Marker 
          position={finalStartPos} 
          title={isDirectionsTab ? "From Location" : "Start Location"} 
          onClick={() => {
            if ((finalStartPos as any).timestamp) {
              setSelectedStop({
                lat: finalStartPos.lat,
                lng: finalStartPos.lng,
                timestamp: (finalStartPos as any).timestamp,
                type: "start"
              });
            }
          }}
          icon={{ 
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, 
            scale: 6, 
            fillColor: "#1a73e8", 
            fillOpacity: 1, 
            strokeWeight: 2, 
            strokeColor: "#FFFFFF", 
            rotation: getArrowRotation() 
          }} 
        />
      )}

      {finalDestPos && isTimestampToday(currentRoutePos?.timestamp) && (
        <OverlayView
          position={finalDestPos}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={() => ({ x: -20, y: -20 })}
        >
          <div 
            onClick={() => { 
              if (currentRoutePos?.timestamp) {
                setSelectedStop({
                  lat: finalDestPos.lat,
                  lng: finalDestPos.lng,
                  timestamp: currentRoutePos.timestamp,
                  type: "current"
                });
              }
            }}
            style={{ 
              position: 'relative', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              width: '40px', 
              height: '40px',
              cursor: 'pointer'
            }}
          >
            <div style={{
              position: 'absolute',
              width: '16px',
              height: '16px',
              backgroundColor: '#d93025', 
              borderRadius: '50%',
              animation: 'location-pulse 1.5s infinite ease-out',
              boxSizing: 'border-box'
            }} />
            <div style={{
              position: 'absolute',
              width: '16px',
              height: '16px',
              backgroundColor: '#d93025',
              borderRadius: '50%',
              boxShadow: '0 0 4px rgba(0,0,0,0.4)',
              zIndex: 2,
              boxSizing: 'border-box'
            }} />
          </div>
        </OverlayView>
      )}

      {institutionData && institutionData.map((inst, index) => {
        if (inst.institution_lat && inst.institution_lng) {
          return (
            <Marker
              key={`institution-${inst.id || index}`}
              position={{ lat: inst.institution_lat, lng: inst.institution_lng }}
              title={inst.institution_name || 'Institution'}
              icon={{
                url: institutionIcon,
                scaledSize: window.google ? new window.google.maps.Size(40, 40) : undefined,
                anchor: window.google ? new window.google.maps.Point(20, 20) : undefined
              }}
            />
          );
        }
        return null;
      })}

    </GoogleMap>
  );
};

const TripDirections: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const handleBackClick = () => {
    if (state?.referrer) {
      navigate(state.referrer);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/vehicles');
    }
  };

  const [state] = useState<any>(() => {
    if (location.state) {
      return location.state;
    }
    
    const stateKey = searchParams.get('stateKey');
    if (stateKey) {
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        try {
          return JSON.parse(storedState);
        } catch (e) {
          console.error("Failed to parse state from local storage");
        }
      }
    }
    return null;
  });
  
  const userData = JSON.parse(localStorage.getItem('userdetails') || 'null');
  const isClientCompanyAdmin = userData?.role === 'client_company_admin';
  const loggedInCompaniesId = userData?.companies_id;
  const vehicleCompaniesId = state?.companies_id || state?.vehicleList?.find((v: any) => String(v.id) === String(state?.vehiclesId))?.companies_id;
  const canEditVehicle = String(loggedInCompaniesId) === String(vehicleCompaniesId);
  const roleFeatures = JSON.parse(localStorage.getItem('roleFeatures') || '[]');
  const hasFeature = (featureName: string) => {
    if (!roleFeatures) return false;
    const target = featureName.toLowerCase();
    const containsFeature = (arr: any[]) => arr.some((f: any) => 
        (typeof f === 'string' && f.toLowerCase() === target) ||
        (f?.name && f.name.toLowerCase() === target) ||
        (f?.feature && f.feature.toLowerCase() === target)
    );

    if (Array.isArray(roleFeatures)) {
        const configObj = roleFeatures.find(item => item && typeof item === 'object' && item.enabled);
        if (configObj) {
            if (configObj.enabled && Array.isArray(configObj.enabled) && containsFeature(configObj.enabled)) return true;
        }
        return containsFeature(roleFeatures);
    }

    if (typeof roleFeatures === 'object') {
        if (roleFeatures.enabled && Array.isArray(roleFeatures.enabled) && containsFeature(roleFeatures.enabled)) return true;
        return !!roleFeatures[featureName] || !!roleFeatures[target];
    }

    return false;
  };

  const hasTripsFeature = hasFeature('trips');

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [originPos, setOriginPos] = useState<{ lat: number; lng: number; timestamp?: string } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
  const [destPos, setDestPos] = useState<{ lat: number; lng: number } | null>(null);
  const [tripPath, setTripPath] = useState<{ lat: number; lng: number }[]>([]);
  const [tripStartPos, setTripStartPos] = useState<{ lat: number; lng: number; timestamp?: string } | null>(null);
  const [tripEndPos, setTripEndPos] = useState<{ lat: number; lng: number } | null>(null);
  const [currentRoutePos, setCurrentRoutePos] = useState<{ lat: number; lng: number; timestamp?: string } | null>(null); 
  const [tripStops, setTripStops] = useState<any[]>([]);
  const [showPolyline, setShowPolyline] = useState(false);
  const [dayWiseData, setDayWiseData] = useState<any[] | null>(null);
  const [showTableInTab, setShowTableInTab] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  const getDefaultTimes = (passedDateStr?: string) => {
    let targetDate = new Date();
    
    if (passedDateStr) {
      const parsedDate = new Date(passedDateStr.replace(' ', 'T'));
      if (!isNaN(parsedDate.getTime())) {
        targetDate = parsedDate;
      }
    }

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');

    return {
      start: `${year}-${month}-${day}T00:00`,
      end: `${year}-${month}-${day}T23:59`
    };
  };

  const [fromDate, setFromDate] = useState<string>(() => getDefaultTimes(state?.rawStartTime).start);
  const [toDate, setToDate] = useState<string>(() => getDefaultTimes(state?.rawStartTime).end);
    
  const [showDatePicker, setShowDatePicker] = useState(true);
  const [viewMode, setViewMode] = useState<'map' | 'history' | 'trips'>('history');
    
  const [openTripsDrawer, setOpenTripsDrawer] = useState(false);
  const [originText, setOriginText] = useState('');
  const [destText, setDestText] = useState('');
  const [distance, setDistance] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [isTripOngoing, setIsTripOngoing] = useState(false);
  const [currentTripData, setCurrentTripData] = useState<any>(null);
  const [openTripModal, setOpenTripModal] = useState(false);
  const [tripActionType, setTripActionType] = useState<'start' | 'end' | 'handover'>('start');
  const [fetchedVehicle, setFetchedVehicle] = useState<any>(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [institutionData, setInstitutionData] = useState<any[]>([]);
  const originAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const destAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const initialFetchDone = useRef(false);
  
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc', 
    libraries,
  });

  const fetchVehicleDetails = () => {
    if (!state?.vehicleNo) return;
    let filterPayload: any = { active: 1 };
    if (state?.activeMobileFilter === 'Absent') {
        filterPayload = { absent: 1 };
    } else if (state?.activeMobileFilter === 'Inactive') {
        filterPayload = { active: 0 };
    }
    setLoadingVehicle(true);
    getVehicle(
      10,
      0,
      (response: any) => {
        const vehicle = response?.message?.vehicles_list?.find((v: any) => v.number_plate === state.vehicleNo) 
                        || response?.message?.vehicles_list?.[0];
        setFetchedVehicle(vehicle);
        setLoadingVehicle(false);
      },
      (err: any) => {
        toast.error("Could not fetch vehicle details");
        setLoadingVehicle(false);
      },
      filterPayload,
      state.vehicleNo
    );
  };
  
  useEffect(() => {
    const compId = String(vehicleCompaniesId);
    if ((compId === '95' || compId === '11') && state?.vehiclesId) {
      getInstitutionVehicleMapping(
        [state.vehiclesId], 
        null,              
        50,                
        0,                 
        (response: any) => {
          if (response?.status === 200 && response?.data) {
            setInstitutionData(response.data);
          }
        },
        (error: any) => {
          console.error("Institution Driver Mapping Error:", error);
        }
      );
    }
  }, [vehicleCompaniesId, state?.vehiclesId]);

  useEffect(() => {
    if (map && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      if (tripPath && tripPath.length > 0) {
        tripPath.forEach((p) => bounds.extend(p));
        tripStops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
        hasPoints = true;
      }
      if (institutionData && institutionData.length > 0) {
        institutionData.forEach((inst) => {
          if (inst.institution_lat && inst.institution_lng) {
            bounds.extend({ lat: inst.institution_lat, lng: inst.institution_lng });
            hasPoints = true;
          }
        });
      }

      if (hasPoints) {
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
           map.panTo(bounds.getNorthEast());
           map.setZoom(15); 
        } else {
           const padding = isDesktop 
             ? { top: 100, bottom: 50, left: 50, right: 450 }
             : { top: 50, bottom: 320, left: 30, right: 30 };
           map.fitBounds(bounds, padding);
        }
      } else if (!isRouteLoading) {
        const fallbackPos = destPos || originPos || DEFAULT_CENTER;
        map.panTo(fallbackPos);
        map.setZoom(15);
      }
    }
  }, [map, tripPath, tripStops, institutionData, destPos, originPos, isDesktop, isRouteLoading]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .gm-ui-hover-effect {
        display: none !important;
      }
      /* Adjust padding: Top Right Bottom Left */
      .gm-style .gm-style-iw-c {
        padding: 4px 10px 8px 10px !important; 
        border-radius: 8px !important;
      }
      .gm-style .gm-style-iw-d {
        overflow: hidden !important; 
        padding: 0 !important;
      }
      @keyframes location-pulse {
        0% { transform: scale(1); opacity: 0.8; }
        100% { transform: scale(3.5); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const formatDateDDMMYYYY = (dateString: string) => {
      if (!dateString) return '';
      const parts = dateString.split('-');
      if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateString;
  };

  useEffect(() => {
    if (!state?.vehiclesId) return;

    getCurrentLocation(
      (response: any) => {
        if (response.status === 200 && response.data && response.data.length > 0) {
          const locationData = response.data[0];
          const lat = Number(locationData.lat);
          const lng = Number(locationData.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            const newDest = { lat, lng };
            setDestText(`${lat}, ${lng}`);
            setDestPos(newDest);
            setMapCenter(newDest); 
          }
        }
      },
      (errMessage: any) => {
        toast.error(typeof errMessage === 'string' ? errMessage : "Failed to update location");
      },
      [state.vehiclesId]
    );
  }, [state?.vehiclesId]);

  const fetchCurrentTrip = () => {
    getCurrentTrip(
      (response: any) => {
        if (response?.status === 404 || response?.message?.errors) {
              setCurrentTripData(null);
              setIsTripOngoing(false);
        } else {
              const trip = response?.message;
              setCurrentTripData(trip);
              const statusLower = trip?.status?.toLowerCase() || '';
              setIsTripOngoing(statusLower.includes('started') || statusLower === 'handover');
        }
      },
      () => {
        setIsTripOngoing(false);
        setCurrentTripData(null);
      },
      '',
      state?.vehiclesId
    );
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
  };

  const handleEditClick = () => {
    if (state?.vehicleList && Array.isArray(state.vehicleList) && state?.vehiclesId) {
      const currentVehicle = state.vehicleList.find((v: any) => String(v.id) === String(state.vehiclesId));
      if (currentVehicle) {
        setFetchedVehicle(currentVehicle);
        setOpenEditModal(true);
        return;
      }
    }
    
    fetchVehicleDetails();
    setOpenEditModal(true);
  };

  const handleFetchTripRoute = (startStr: string, endStr: string) => {
    if (!state?.vehiclesId) return toast.error("Vehicle ID not found");

    setTripStartPos(null);
    setTripEndPos(null);
    setCurrentRoutePos(null);
    setTripPath([]);
    setTripStops([]);
    setDayWiseData(null);
    setShowTableInTab(false);
    setSelectedDate(null);

    const formatForApi = (dateStr: string) => {
      if (!dateStr) return '';
      return dateStr.replace('T', ' ') + ':00';
    };

    const startDateTime = formatForApi(startStr);
    const endDateTime = formatForApi(endStr);

    const resetToCurrentLocation = () => {
      
      setTripPath([]);
      setTripStops([]);
      setShowPolyline(false);
      setDirections(null);
      const fallbackPos = destPos || originPos || DEFAULT_CENTER;
      setMapCenter(fallbackPos);

      if (map && window.google) {
        map.panTo(fallbackPos);
        map.setZoom(15);
      }
    };

    setIsRouteLoading(true);

    getVehicleRoute(
      state.vehiclesId,
      startDateTime,
      endDateTime,
      (response: any) => {
        setIsRouteLoading(false);

        const routeData = response?.data || response;

      if (routeData?.day_wise_distance && Array.isArray(routeData.day_wise_distance)) {
          const filteredDayWise = routeData.day_wise_distance.filter(
            (day: any) => Number(day.distance_km) >= 1
          );
          
          setDayWiseData(filteredDayWise);
          
          if (filteredDayWise.length > 0) {
            const initialRow = filteredDayWise[0];
            handleRowClick(initialRow.date);
          } else {
            setShowTableInTab(true);
            setShowPolyline(false);
          }
          return;
        }

        if (routeData) {
          if (routeData.distance_km !== undefined) {
            setDistance(`${Number(routeData.distance_km).toFixed(2)} km`);
          }
          if (routeData.time_taken) {
            const { seconds, hours } = routeData.time_taken;
            if (seconds) {
              const h = Math.floor(Number(seconds) / 3600);
              const m = Math.floor((Number(seconds) % 3600) / 60);
              setDuration(`${h}h ${m}m`);
            } else if (hours) {
              setDuration(`${Number(hours).toFixed(1)} hrs`);
            }
          }
        }

        if (routeData?.start?.lat && routeData?.start?.lng) {
          setTripStartPos({ 
            lat: Number(routeData.start.lat), 
            lng: Number(routeData.start.lng), 
            timestamp: routeData.start.timestamp || routeData.start_time || startDateTime
          });
        }
        if (routeData?.end?.lat && routeData?.end?.lng) {
          setTripEndPos({ lat: Number(routeData.end.lat), lng: Number(routeData.end.lng) });
        }
        if (routeData?.current_location?.lat && routeData?.current_location?.lng) {
          setCurrentRoutePos({ 
            lat: Number(routeData.current_location.lat), 
            lng: Number(routeData.current_location.lng), 
            timestamp: routeData.current_location.timestamp 
          });
        }

        if (routeData) {
          const filteredStops = processAndFilterStops(
              routeData.stop_events || [], 
              routeData.idle_events || [],
              routeData.first_moving_event || []
          );
          setTripStops(filteredStops);
        }

        if (routeData?.polyline && window.google?.maps) {
          try {
            const decodedPath = window.google.maps.geometry.encoding.decodePath(routeData.polyline);
            const formattedPath = decodedPath.map((latLng) => ({
              lat: latLng.lat(),
              lng: latLng.lng()
            }));

            setTripPath(formattedPath);
            setShowPolyline(true);
            setDirections(null);
          } catch (error) {
            console.error("Error decoding polyline:", error);
          }
        } else {
          if (!routeData?.day_wise_distance) {
            toast.info("No route data found.");
            resetToCurrentLocation();
          }
        }
      },
      (error: any) => {
        setIsRouteLoading(false);
        const msg = error?.details?.message || error?.message || "Error fetching route";
        toast.error(msg);
        resetToCurrentLocation();
      }
    );
  };

  const handleMapClick = () => {
    setViewMode('map');
    setShowPolyline(false); 
    setTripStops([]); 
    setShowDatePicker(false);
    setOpenTripsDrawer(false); 
    setDayWiseData(null);
    setShowTableInTab(false);
    setSelectedDate(null);
    setDistance('');
    setDuration('');
    if (originPos) {
        setMapCenter(originPos); 
        
        if (map && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(originPos);
            if (destPos) bounds.extend(destPos);
            map.fitBounds(bounds);
        }
    }
  };

  const handleHistoryClick = () => {
    setViewMode('history');
    setShowDatePicker(true); 
    setOpenTripsDrawer(false);
    setShowTableInTab(false);
    handleFetchTripRoute(fromDate, toDate); 
  };

  const handleTripsClick = () => {
    setViewMode('trips');
    setOpenTripsDrawer(true);
    setShowDatePicker(false); 
    setShowTableInTab(false);
    setSelectedDate(null);
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newDate = event.target.value;
    const maxDateTime = getCurrentDateTimeLocal();

    if (new Date(newDate) > new Date(maxDateTime)) {
      newDate = maxDateTime;
    }

    setFromDate(newDate);

    if (new Date(newDate) > new Date(toDate)) {
      setToDate(newDate);
      handleFetchTripRoute(newDate, newDate);
    } else {
      handleFetchTripRoute(newDate, toDate);
    }
    
    event.target.blur(); 
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newDate = event.target.value;
    const maxDateTime = getCurrentDateTimeLocal();

    if (new Date(newDate) > new Date(maxDateTime)) {
      newDate = maxDateTime;
    }

    if (new Date(newDate) < new Date(fromDate)) {
      toast.warning("To date cannot be earlier than From date.");
      newDate = fromDate;
    }

    setToDate(newDate);
    handleFetchTripRoute(fromDate, newDate);
    event.target.blur();
  };

  const handleRowClick = (dateStr: string, autoTriggerOpts?: { start: string; end: string }) => {
    setSelectedDate(dateStr);
    
    if (!state?.vehiclesId) return toast.error("Vehicle ID not found");

    let startDateTime = `${dateStr} 00:00:00`;
    let endDateTime = `${dateStr} 23:59:59`;

    if (autoTriggerOpts) {
      const fromDateOnly = autoTriggerOpts.start.split('T')[0];
      const toDateOnly = autoTriggerOpts.end.split('T')[0];
      
      let startTime = "00:00:00";
      let endTime = "23:59:59";
      
      if (dateStr === fromDateOnly) {
        startTime = autoTriggerOpts.start.split('T')[1] + ":00";
      }
      if (dateStr === toDateOnly) {
        endTime = autoTriggerOpts.end.split('T')[1] + ":59";
      }
      
      startDateTime = `${dateStr} ${startTime}`;
      endDateTime = `${dateStr} ${endTime}`;
    } else {
      setFromDate(`${dateStr}T00:00`);
      setToDate(`${dateStr}T23:59`);
    }

    setIsRouteLoading(true);
    getVehicleRoute(
      state.vehiclesId,
      startDateTime,
      endDateTime,
      (response: any) => {
        setIsRouteLoading(false);

        const routeData = response?.data || response;
        
        if (routeData) {
            setShowTableInTab(true);

            if (routeData.distance_km !== undefined) {
              setDistance(`${Number(routeData.distance_km).toFixed(2)} km`);
            }
            if (routeData.time_taken) {
              const { seconds, hours } = routeData.time_taken;
              if (seconds) {
                const h = Math.floor(Number(seconds) / 3600);
                const m = Math.floor((Number(seconds) % 3600) / 60);
                setDuration(`${h}h ${m}m`);
              } else if (hours) {
                setDuration(`${Number(hours).toFixed(1)} hrs`);
              }
            }

            if (routeData.start?.lat && routeData.start?.lng) {
                setTripStartPos({ 
                  lat: Number(routeData.start.lat), 
                  lng: Number(routeData.start.lng),
                  timestamp: routeData.start.timestamp || routeData.start_time || startDateTime
                });
            } else { setTripStartPos(null); }

            if (routeData.end?.lat && routeData.end?.lng) {
                setTripEndPos({ lat: Number(routeData.end.lat), lng: Number(routeData.end.lng) });
            } else { setTripEndPos(null); }

            if (routeData.current_location?.lat && routeData.current_location?.lng) {
                setCurrentRoutePos({ 
                  lat: Number(routeData.current_location.lat), 
                  lng: Number(routeData.current_location.lng),
                  timestamp: routeData.current_location.timestamp
                });
            } else { setCurrentRoutePos(null); }

            if (routeData) {
                const filteredStops = processAndFilterStops(
                  routeData.stop_events || [], 
                  routeData.idle_events || [],
                  routeData.first_moving_event || []
                );
                setTripStops(filteredStops);
            } else {
                setTripStops([]);
            }

            if (routeData.polyline && window.google && window.google.maps) {
                try {
                    const decodedPath = window.google.maps.geometry.encoding.decodePath(routeData.polyline);
                    const formattedPath = decodedPath.map((latLng) => ({
                        lat: latLng.lat(), lng: latLng.lng()
                    }));
              
                    setTripPath(formattedPath);
                    setShowPolyline(true);
                    setDirections(null); 
                    
                } catch (error) {
                    console.error("Error decoding polyline:", error);
                }
            } else {
                setTripPath([]);
                setShowPolyline(false);
            }
        } else {
            setTripPath([]);
            setShowPolyline(false);
            setTripStops([]);
            setTripStartPos(null);
            setTripEndPos(null);
            setCurrentRoutePos(null);
        }
      },
      (error: any) => {
          setIsRouteLoading(false);
          toast.error("Error fetching route for selected day")
      }
    );
  };

  const handleInputClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const input = e.target as HTMLInputElement;
      if (input.tagName === 'INPUT' && 'showPicker' in input) {
          try {
              (input as any).showPicker();
          } catch (error) {
              console.log("Picker not supported programmatically");
          }
      }
  };

  useEffect(() => { 
    if (state && hasTripsFeature) {
      fetchCurrentTrip(); 
    }
  }, [openEditModal, hasTripsFeature]);

  useEffect(() => {
    if (!state) { 
        navigate(-1); 
        return; 
    }
    if (isLoaded && window.google) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const currentPos = { lat: position.coords.latitude, lng: position.coords.longitude };          
          setOriginPos(currentPos);
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: currentPos }, (results, status) => {
            if (status === 'OK' && results?.[0]) setOriginText(results[0].formatted_address);
          });
          if (viewMode !== 'history') {
             setMapCenter(currentPos);
          }
        });
      }
    }
  }, [isLoaded, state]);

  useEffect(() => {
    if (isLoaded && viewMode === 'history' && map && !initialFetchDone.current) {
      initialFetchDone.current = true;
      handleFetchTripRoute(fromDate, toDate);
    }
  }, [isLoaded, map, viewMode]);

  useEffect(() => {
    if (viewMode === 'map' && isLoaded && originPos && destPos && directionsServiceRef.current && !showPolyline && !dayWiseData) {
      directionsServiceRef.current.route(
        {
          origin: originPos,
          destination: destPos,
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === 'OK' && result) {
            setDirections(result);
            const leg = result.routes[0]?.legs[0];
            if (leg) {
              setDistance(leg.distance?.text || '');
              setDuration(leg.duration?.text || '');
            }
          }
        }
      );
    }
  }, [originPos, destPos, isLoaded, showPolyline, viewMode, dayWiseData]);

  const buttonStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textTransform: 'none',
    padding: '8px 12px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    '& .MuiButton-startIcon': {
        marginRight: 0,
        marginLeft: 0
    }
  };

  const getActiveStyle = (mode: string) => {
      const isActive = viewMode === mode;
      return {
          ...buttonStyle,
          borderColor: isActive ? '#1a73e8' : '#5f6368',
          color: isActive ? '#1a73e8' : '#5f6368',
          bgcolor: isActive ? '#e8f0fe' : 'transparent',
          '&:hover': {
              bgcolor: isActive ? '#e8f0fe' : '#f5f5f5'
          }
      };
  };

  const noCalendarIconStyle = {
    '& input::-webkit-calendar-picker-indicator': {
      display: 'none',
      WebkitAppearance: 'none',
    },
    '& input': {
        cursor: 'pointer'
    }
  };

  const renderTripActionButton = () => {
    if (!currentTripData ) {
        return (
            <Button 
                variant="contained" 
                onClick={() => { 
                    setTripActionType('start'); 
                    setOpenTripModal(true); 
                }} 
                startIcon={<PlayArrowIcon />} 
                sx={{ ...buttonStyle, bgcolor: '#2e7d32' }}
            >
                Start Trip
            </Button>
        );
    }

    const status = currentTripData.status;
    const initiatedBy = currentTripData.initiated_by;
    const isHandover = status === 'handover';
    const isDriver = initiatedBy === 'driver' || !initiatedBy;
    const isVendor = initiatedBy === 'vendor_supervisor' || initiatedBy === 'vendor_company_admin';

    if (isClientCompanyAdmin && isHandover) {
        return (
            <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'orange', color: 'white' }} size="small" 
                startIcon={<HandshakeIcon />}
                onClick={() => { 
                    setTripActionType('handover'); 
                    setOpenTripModal(true); 
                }}>
                Handover Accept
            </Button>
        );
    }

    if (!isClientCompanyAdmin) {
        if (isHandover) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'orange', color: 'white' }} size="small" 
                    startIcon={<HandshakeIcon />}
                    onClick={() => { 
                        setTripActionType('handover'); 
                        setOpenTripModal(true); 
                    }}>
                    Handover Accept
                </Button>
            );
        }
        if (isDriver) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'red', color: 'white' }} size="small" 
                    startIcon={<StopIcon />}
                    onClick={() => { 
                        setTripActionType('end'); 
                        setOpenTripModal(true); 
                    }}>
                    End Trip
                </Button>
            );
        }
        if (isVendor) {
            return (
                <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: '#ef6363', color: 'white' }} size="small" 
                    startIcon={<StopIcon />}
                    onClick={() => { 
                        setTripActionType('end'); 
                        setOpenTripModal(true); 
                    }}>
                    End Trip
                </Button>
            );
        }
        return (
            <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'purple', color: 'white' }} size="small" 
                startIcon={<PriorityHighIcon />}
                onClick={() => { 
                    setTripActionType('end');
                    setOpenTripModal(true); 
                }}>
                Forced Handover
            </Button>
        );
    }
    return (
        <Button variant="contained" sx={{ ...buttonStyle, backgroundColor: 'red', color: 'white' }} size="small" onClick={() => { setTripActionType('end'); setOpenTripModal(true); }}>
            End Trip
        </Button>
    );
  };

  if (!isLoaded || !state) return <Box p={3}>Loading Map...</Box>;

  return (
    <>
      {isDesktop && <Header />}
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {isDesktop && <NavBar selected='Vehicles' />}
        
        <Box sx={{ flexGrow: 1, position: 'relative', height: '100%', marginLeft: isDesktop ? '172px' : 0, marginTop: isDesktop ? '60px' : 0 }}>
          {(!isDesktop ) && (
            <IconButton 
              onClick={handleBackClick} 
              sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  left: 12, 
                  zIndex: 20, 
                  background: 'linear-gradient(45deg, black, #ed1b1b)',
                  color: 'white', 
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <ArrowBackIcon sx={{ color: '#ffffff' }} />
            </IconButton>
          )}

          <SharedGoogleMap 
            mapContainerStyle={containerStyle}
            mapCenter={mapCenter}
            options={{ 
              disableDefaultUI: true, 
              zoomControl: true,
              zoomControlOptions: { position: window.google?.maps?.ControlPosition?.RIGHT_CENTER },
              clickableIcons: false
            }}
            onLoad={setMap}
            directions={directions}
            showPolyline={showPolyline}
            tripPath={tripPath}
            tripStops={tripStops}
            tripStartPos={tripStartPos}
            tripEndPos={tripEndPos}
            originPos={originPos}
            destPos={destPos}
            currentRoutePos={currentRoutePos}
            isDirectionsTab={viewMode === 'map'}
            institutionData={institutionData}
            
          />

          {isRouteLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.6)', 
                zIndex: 5,
              }}
            >
              <CircularProgress size={60} thickness={4} />
            </Box>
          )}

          {viewMode === 'map' && !dayWiseData && (
            <Paper 
              elevation={4} 
              sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  left: 64, 
                  right: 12, 
                  zIndex: 10, 
                  padding: '12px', 
                  borderRadius: '12px',
                  maxWidth: { md: '450px' },
              }}
            >
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Autocomplete onLoad={(a) => (originAutocompleteRef.current = a)} onPlaceChanged={() => {
                            const place = originAutocompleteRef.current?.getPlace();
                            if (place?.geometry?.location) {
                                const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
                                setOriginPos(newPos);
                                setOriginText(place.formatted_address || "");
                                setMapCenter(newPos);
                                handleMapClick(); 
                            }
                        }}>
                        <Box display="flex" alignItems="center" sx={{ bgcolor: '#e8f0fe', borderRadius: '8px', padding: '2px 12px' }}>
                            <NearMeIcon sx={{ fontSize: 20, color: '#1a73e8', mr: 1 }} />
                            <InputBase value={originText} onChange={(e) => setOriginText(e.target.value)} placeholder="From Location" fullWidth sx={{ fontSize: '0.9rem' }} />
                            {originText.length > 0 && (
                                <IconButton 
                                    size="small" 
                                    onClick={() => {
                                        setOriginText('');
                                        setDirections(null); 
                                    }}
                                    sx={{ p: '4px' }}
                                >
                                    <CloseIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                                </IconButton>
                            )}
                        </Box>
                    </Autocomplete>

                    <Autocomplete onLoad={(a) => (destAutocompleteRef.current = a)} onPlaceChanged={() => {
                            const place = destAutocompleteRef.current?.getPlace();
                            if (place?.geometry?.location) {
                                setDestPos({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
                                setDestText(place.formatted_address || "");
                                handleMapClick(); 
                            }
                        }}>
                        <Box display="flex" alignItems="center" sx={{ bgcolor: '#fce8e6', borderRadius: '8px', padding: '2px 12px' }}>
                            <CircleIcon sx={{ fontSize: 14, color: '#d93025', mr: 1.5, ml: 0.5 }} />
                            <InputBase value={destText} onChange={(e) => setDestText(e.target.value)} placeholder="Destination" fullWidth sx={{ fontSize: '0.9rem' }} />
                        </Box>
                    </Autocomplete>
                </Box>
            </Paper>
          )}

          <Draggable nodeRef={dragRef} disabled={!isDesktop}>
            <Paper 
              ref={dragRef}
              elevation={6} 
              sx={{ 
                position: 'fixed', 
                bottom: isDesktop ? 'auto' : 0, 
                top: isDesktop ? 80 : 'auto', 
                left: isDesktop ? 'auto' : 0, 
                right: isDesktop ? '20px' : 0,
                width: isDesktop ? '400px' : '100%',
                padding: '12px', 
                borderRadius: isDesktop ? '16px' : '16px 16px 0 0',
                zIndex: 10,
                cursor: isDesktop ? 'grab' : 'default', 
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMinimized ? 0 : 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{color:'black'}}>
                      {state?.vehicleNo}
                    </Typography>
                    {state?.driverName && (
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        • {state?.driverName}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {(distance || duration) && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {distance && (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a73e8', fontSize: '14px' }}>
                            🚗 {distance}
                          </Typography>
                        )}
                        {duration && (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a73e8', fontSize: '14px' }}>
                            ⏱ {duration}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {!isDesktop && (
                      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        {canEditVehicle && (
                            <FaFilePen onClick={handleEditClick} style={{ cursor: 'pointer', fontSize: '22px', color: '#1976d2' }} />
                        )}
                        <CallIcon onClick={() => window.location.href = `tel:${state.driverPhone}`} sx={{ color: '#4a81c5', cursor: 'pointer', fontSize: '24px' }} />
                      </Box>
                    )}
                  </Box>
                </Box>

                <IconButton size="small" onClick={() => setIsMinimized(!isMinimized)} sx={{ p: 0.5 }}>
                  {isDesktop ? (
                    isMinimized ? <ExpandMoreIcon fontSize="large" /> : <ExpandLessIcon fontSize="large" />
                  ) : (
                    isMinimized ? <ExpandLessIcon fontSize="large" /> : <ExpandMoreIcon fontSize="large" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={!isMinimized} timeout="auto" unmountOnExit>
                <Box sx={{ padding: '0px' }}>
                  {showDatePicker && (
                    <Stack direction="row" spacing={2} sx={{ marginBottom:'12px', marginTop: '12px' }}>
                      <TextField 
                        label="From" 
                        type="datetime-local" 
                        value={fromDate} 
                        onChange={handleFromDateChange} 
                        onClick={handleInputClick}
                        fullWidth 
                        size="small" 
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: getCurrentDateTimeLocal() }}
                        sx={noCalendarIconStyle} 
                      />
                      <TextField 
                        label="To" 
                        type="datetime-local" 
                        value={toDate} 
                        onChange={handleToDateChange} 
                        onClick={handleInputClick}
                        fullWidth 
                        size="small" 
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: fromDate, max: getCurrentDateTimeLocal() }}
                        sx={noCalendarIconStyle} 
                      />
                    </Stack>
                  )}

                  {dayWiseData && showTableInTab && (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        maxHeight: '150px', 
                        overflowY: 'auto', 
                        borderRadius: '8px',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '4px' }
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ '& th': { bgcolor: '#f1f3f4', py: 0.5, fontSize: '0.8rem' } }}>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell align="right"><strong>Distance</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dayWiseData.map((row, index) => (
                            <TableRow 
                              key={index} 
                              hover
                              selected={selectedDate === row.date}
                              onClick={() => handleRowClick(row.date)}
                              sx={{ 
                                cursor: 'pointer', 
                                '& td': { py: 0.5, fontSize: '0.8rem' },
                                '&.Mui-selected': { backgroundColor: '#e8f0fe' },
                                '&.Mui-selected:hover': { backgroundColor: '#d2e3fc' },
                                '&:hover': { backgroundColor: '#f1f3f4' }
                              }}
                            >
                              <TableCell>{formatDateDDMMYYYY(row.date)}</TableCell>
                              <TableCell align="right">{row.distance_km} km</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}

                  <Stack 
                    direction="row" 
                    spacing={1.5} 
                    alignItems="center"
                    justifyContent="flex-start" 
                    sx={{ mt: 1, overflowX: 'auto' }}
                  >
                    <Button variant="outlined" onClick={handleHistoryClick} startIcon={<FileClockOutline />} sx={getActiveStyle('history')}>
                      History
                    </Button>
                    <Button variant="outlined" onClick={handleMapClick} startIcon={<img src={mapsIcon} alt="Map" style={{ width: '24px', height: '24px' }} />} sx={getActiveStyle('map')}>
                      Directions
                    </Button>
                    
                    {hasTripsFeature && (
                      <>
                        <Button variant="outlined" onClick={handleTripsClick} startIcon={<ListAltIcon />} sx={getActiveStyle('trips')}>
                          Trips
                        </Button>
                        <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                          {renderTripActionButton()}
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          </Draggable>
        </Box>

        {hasTripsFeature && (
            <TripDetailsModal 
                isOpen={openTripModal} 
                onClose={() => { setOpenTripModal(false); fetchCurrentTrip(); }} 
                type={tripActionType} 
                tripId={state.tripId} 
                vehicleId={state.vehiclesId} 
                vehicleList={state.vehicleList || []} 
                orderPicked={state.ordersPicked} 
                orderDelivered={state.ordersDelivered} 
            />
        )}
        <EditVehicleModal
            open={openEditModal}
            onClose={handleCloseModal}
            vehicle={fetchedVehicle}
            tab={'active'}
        />
        {hasTripsFeature && (
            <TripsListDrawer 
              open={openTripsDrawer}
              onClose={() => { 
                  setOpenTripsDrawer(false); 
                  if (directions) {
                      setViewMode('map');
                  } else {
                      setViewMode('history');
                      setShowDatePicker(true);
                  }
              }}
              vehicleId={state?.vehiclesId}
              defaultVehicleNo={state?.vehicleNo}
              defaultDriverName={state?.driverName}
              defaultDriverPhone={state?.driverPhone}
            />
        )}
      </Box>
    </>
  );
};

export default TripDirections;